import { useCallback, useLayoutEffect, useRef } from 'react'
import { classNames, legacyContractDataAttrs, legacyCssClass } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorSettingsTab } from '@/msgdeskRuntime/contracts/editorModel'
import { useEditorFormModelSnapshot, useEditorSection } from '../../EditorTemplateContract'
import { useEditorSurface } from '../../EditorSurfaceContext'
import { DisplayPanel } from './DisplayPanel'
import { RulesPanel } from './RulesPanel'
import { EmailPanel } from './EmailPanel'
import { CountsPanel } from './CountsPanel'
import { AvatarPanel } from './AvatarPanel'
import { PagePanel } from './PagePanel'
import { ProtectPanel } from './ProtectPanel'
import { RulePanel } from './RulePanel'
import { projectSettingSelectDom, reassertSettingSelectDisabled, syncSettingSelectDom } from './SettingSelectState'

// Configure panel — tabbed strip of fieldsets. Tabs are React-driven (one pane
// visible at a time); all panes stay mounted (hidden via the skin token) so
// every input is still in the form for Save. Contracts + input names are kept
// so legacy reads the values. All visual classes come from the surface skin.
export const settingsSectionTabs: Array<{ id: EditorSettingsTab; label: string }> = [
  { id: 'display', label: 'Display' },
  { id: 'rules', label: 'Rules' },
  { id: 'email', label: 'Email' },
  { id: 'counts', label: 'Counts' },
  { id: 'timers', label: 'Timers' },
  { id: 'protect', label: 'Protection' },
  { id: 'avatar', label: 'Avatar' },
  { id: 'page', label: 'Page' },
]

const tabs = settingsSectionTabs
const settingsContract = legacyContractDataAttrs('editor.settings.more')

// Configure's React pane navigation owns panel visibility. Legacy m31 still
// addresses these fieldsets for values and can leave a stale Tailwind
// `hidden` class on a panel while switching its retired numeric tabs.
function projectSettingsPanelVisibility(root: HTMLElement) {
  for (const fieldset of Array.from(root.getElementsByTagName('fieldset'))) {
    if (fieldset.hidden) fieldset.hidden = false
    if (fieldset.classList.contains('hidden')) fieldset.classList.remove('hidden')
    if (fieldset.style.display === 'none') fieldset.style.display = ''
  }
}

// Without props the panel keeps its original self-driven tab strip (mailbox,
// legacy inline callers). The shared Configure modal supplies `activePane`
// (its own React-local nav state) and hides the strip with `showTabs={false}`.
export function SettingsInputs({
  activePane,
  showTabs = true,
}: {
  activePane?: EditorSettingsTab
  showTabs?: boolean
} = {}) {
  const { skin } = useEditorSurface()
  const rootRef = useRef<HTMLDivElement>(null)
  const formModel = useEditorFormModelSnapshot()
  const settingsSection = useEditorSection('settings')
  const active = activePane || settingsSection.state.activeTab || 'display'
  const paneClass = (id: EditorSettingsTab) => classNames(
    'md-settings__pane',
    active !== id && 'md-settings__pane--hidden',
    active !== id && skin.settings.paneHidden,
  )
  const syncSettingElement = (root: HTMLElement, target: EventTarget) => {
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      syncSettingSelectDom(root, target)
      dispatchEditorAction({ action: 'sync-input', target, reason: 'react-settings:event' })
      projectSettingsPanelVisibility(root)
      requestAnimationFrame(() => projectSettingsPanelVisibility(root))
    }
  }

  useLayoutEffect(() => {
    if (rootRef.current) {
      projectSettingSelectDom(rootRef.current, formModel)
      projectSettingsPanelVisibility(rootRef.current)
    }
  }, [active, formModel])

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return
    // 'disabled' is watched because legacy's async settings pass writes
    // attr("disabled", …) onto ANY pair's select from its own stale value
    // slots (through the chrome projection) — the per-pair one-frame guard in
    // syncSettingSelectDom only covers the pair the user just touched, so a
    // cross-pair write (change Type of answers → is_view_combo disabled)
    // survived. reassertSettingSelectDisabled restores every pair's combo
    // disabled state from its master CHECKBOX (the React-controlled source of
    // truth, which legacy never writes) — never from a captured model
    // snapshot, which at observer timing may lag the user's just-made click
    // and revert it. Difference-guarded writes, so the observer converges.
    const observer = new MutationObserver(() => {
      projectSettingsPanelVisibility(root)
      reassertSettingSelectDisabled(root)
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style', 'disabled'],
      subtree: true,
    })
    projectSettingsPanelVisibility(root)
    return () => observer.disconnect()
  }, [])

  const assignRootRef = useCallback((node: HTMLDivElement | null) => {
    rootRef.current = node
    settingsContract.ref(node)
  }, [])

  return (
    <div
      {...settingsContract}
      ref={assignRootRef}
      className={classNames(legacyCssClass('editor.settings.tools'), 'md-settings', skin.settings.root)}
      onChange={event => syncSettingElement(event.currentTarget, event.target)}
      onInput={event => syncSettingElement(event.currentTarget, event.target)}
    >
      {showTabs ? (
        <div className={classNames('md-settings__tabs', skin.settings.tabs)}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={classNames(
                'md-settings__tab',
                skin.settings.tab,
                active === tab.id && 'md-settings__tab--active',
                active === tab.id ? skin.settings.tabActive : skin.settings.tabInactive,
              )}
              onClick={() => settingsSection.update({ activeTab: tab.id }, 'editor-settings:tab')}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className={classNames('md-settings__body', skin.settings.body)}>
        <div className={paneClass('display')}><DisplayPanel /></div>
        <div className={paneClass('rules')}><RulesPanel /></div>
        <div className={paneClass('email')}><EmailPanel /></div>
        <div className={paneClass('counts')}><CountsPanel /></div>
        <div className={paneClass('timers')}><RulePanel /></div>
        <div className={paneClass('protect')}><ProtectPanel /></div>
        <div className={paneClass('avatar')}><AvatarPanel /></div>
        <div className={paneClass('page')}><PagePanel /></div>
      </div>
    </div>
  )
}
